/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import EXIF from 'exif-js';
import { api } from "../api";
import LocationPickerMap from "../components/LocationPickerMap";

function isTokenValid(token) {
  try {
    const { exp } = jwtDecode(token);
    return exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

function getValidToken() {
  const lpoToken = localStorage.getItem("lpo_token");
  const userToken = localStorage.getItem("fluxonix_token");

  const lpoValid = lpoToken && isTokenValid(lpoToken);
  const userValid = userToken && isTokenValid(userToken);

  if (lpoValid) return { token: lpoToken, userType: "lpo" };
  if (userValid) return { token: userToken, userType: "user" };
  return null;
}

export default function LPOFormManual() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    supplier_name: "",
    company_name: "",
    contact_number: "",
    state: "",
    location: "",
    material_list: "",
    monthly_quantity_tons: "",
    price_per_unit: "",
    material_form: "",
    sector: "",
    industry: "",
    remarks: "",
    submitted_by_team: "LPO",
    submission_data: {},
  });

  const [submitted, setSubmitted] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [error, setError] = useState("");
  const [photoHasGPS, setPhotoHasGPS] = useState(true);
  const [fallbackZone, setFallbackZone] = useState("");
  const [markerPosition, setMarkerPosition] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const result = getValidToken();
    if (!result) {
      setTimeout(() => navigate("/otp-login"), 0);
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = function () {
      const img = new Image();
      img.onload = function () {
        EXIF.getData(img, function () {
          const lat = EXIF.getTag(this, "GPSLatitude");
          const lon = EXIF.getTag(this, "GPSLongitude");
          if (lat && lon) {
            setPhotoHasGPS(true);
          } else {
            setPhotoHasGPS(false);
          }
        });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);

    const token = localStorage.getItem("lpo_token");
    if (!token || !isTokenValid(token)) {
      setError("Session expired. Please log in again.");
      navigate("/otp-login");
      return;
    }

    try {
      const payload = {
        ...formData,
        material_list: formData.material_list
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        submission_data: {
          ...(formData.submission_data || {}),
          gps: markerPosition ? {
            latitude: markerPosition.lat,
            longitude: markerPosition.lng,
          } : null,
        },
      };

      const res = await api.post("/lpo/submit-form", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const submissionId = res.data?.submission_id;

      if (submissionId && selectedPhotos && selectedPhotos.length > 0) {
        try {
          const fd = new FormData();
          selectedPhotos.forEach((file) => fd.append("files", file));

          const uploadRes = await fetch("/api/upload-photos", {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          });

          const { urls: uploadedUrls } = await uploadRes.json();

          await api.post(
            "/lpo/update-media-urls",
            {
              submission_id: submissionId,
              photo_urls: uploadedUrls,
              fallback_zone: !photoHasGPS ? fallbackZone : null,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (uploadErr) {
          console.error("Photo upload failed:", uploadErr);
        }
      }

      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError("Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const removePhoto = (index) => {
    setSelectedPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  if (submitted) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-6 max-w-md text-center">
          <span className="material-symbols-outlined text-emerald-500 text-5xl mb-4">check_circle</span>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Submission Received</h3>
          <p className="text-slate-600 dark:text-slate-400">
            Thank you! Your supplier submission has been received and is pending approval.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-8">
      {/* Main Form */}
      <div className="col-span-12 lg:col-span-8">
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
          <h3 className="font-bold text-slate-900 dark:text-white mb-6">Supplier Information</h3>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Supplier Name *</label>
                  <input
                    type="text"
                    name="supplier_name"
                    value={formData.supplier_name}
                    onChange={handleChange}
                    required
                    className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Company Name</label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Contact Number</label>
                  <input
                    type="text"
                    name="contact_number"
                    value={formData.contact_number}
                    onChange={handleChange}
                    className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Location</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Materials</label>
                  <input
                    type="text"
                    name="material_list"
                    value={formData.material_list}
                    onChange={handleChange}
                    placeholder="e.g., HDPE, LDPE, PP"
                    className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
                  />
                  <p className="text-[10px] text-slate-400">Comma-separated list</p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Monthly Quantity (tons)</label>
                  <input
                    type="number"
                    name="monthly_quantity_tons"
                    value={formData.monthly_quantity_tons}
                    onChange={handleChange}
                    className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Price per Unit</label>
                  <input
                    type="text"
                    name="price_per_unit"
                    value={formData.price_per_unit}
                    onChange={handleChange}
                    className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Material Form</label>
                  <input
                    type="text"
                    name="material_form"
                    value={formData.material_form}
                    onChange={handleChange}
                    placeholder="e.g., Granules, Flakes"
                    className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Sector</label>
                    <input
                      type="text"
                      name="sector"
                      value={formData.sector}
                      onChange={handleChange}
                      className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Industry</label>
                    <input
                      type="text"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Remarks - Full Width */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Remarks</label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                rows={3}
                placeholder="Additional notes or context..."
                className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
              />
            </div>

            {/* Location Capture */}
            <div
              className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => setShowMap(!showMap)}
            >
              <div className="size-10 rounded-lg bg-white dark:bg-slate-700 flex items-center justify-center text-primary shadow-sm">
                <span className="material-symbols-outlined">location_on</span>
              </div>
              <div className="flex flex-col flex-1">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">
                  GPS Location
                </span>
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {markerPosition ? 'Location captured' : 'Tap to capture GPS location...'}
                </p>
                {markerPosition && (
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    {markerPosition.lat.toFixed(4)}° N, {markerPosition.lng.toFixed(4)}° W
                  </p>
                )}
              </div>
              {markerPosition && (
                <span className="material-symbols-outlined text-emerald-500">check_circle</span>
              )}
              <span className={`material-symbols-outlined text-slate-400 transition-transform ${showMap ? 'rotate-180' : ''}`}>
                expand_more
              </span>
            </div>

            {showMap && (
              <div className="h-64 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                <LocationPickerMap onLocationChange={setMarkerPosition} />
              </div>
            )}
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
            <span className="material-symbols-outlined text-red-500">error</span>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <button onClick={() => setError("")} className="ml-auto">
              <span className="material-symbols-outlined text-red-400 hover:text-red-600">close</span>
            </button>
          </div>
        )}
      </div>

      {/* Right Panel - Photos */}
      <div className="col-span-12 lg:col-span-4 space-y-8">
        {/* Evidence Assets */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900 dark:text-white">Photo Evidence</h3>
            <span className="text-[10px] font-bold text-slate-400 bg-slate-50 dark:bg-slate-800 px-2 py-0.5 rounded uppercase tracking-widest">
              Optional
            </span>
          </div>

          <label className="flex flex-col items-center gap-4 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-8 bg-slate-50/50 dark:bg-slate-800/30 hover:border-primary/50 transition-colors cursor-pointer">
            <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <span className="material-symbols-outlined text-2xl">add_photo_alternate</span>
            </div>
            <div className="text-center">
              <p className="text-sm font-bold text-slate-900 dark:text-white">Upload photos</p>
              <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-tight font-medium">
                PNG, JPG, HEIC up to 20MB
              </p>
            </div>
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) {
                  setSelectedPhotos((prev) => [...prev, ...files]);
                  if (files[0]) handlePhotoUpload({ target: { files: [files[0]] } });
                }
                e.target.value = "";
              }}
            />
            <span className="w-full py-2 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 rounded-lg text-xs font-bold text-center hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors">
              Browse Files
            </span>
          </label>

          {/* Photo Previews */}
          {selectedPhotos.length > 0 && (
            <div className="mt-4 grid grid-cols-3 gap-2">
              {selectedPhotos.map((file, idx) => (
                <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`preview-${idx}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(idx)}
                    className="absolute top-1 right-1 size-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                  >
                    <span className="material-symbols-outlined text-xs">close</span>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Fallback zone if no GPS */}
          {!photoHasGPS && selectedPhotos.length > 0 && (
            <div className="mt-4 space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Fallback Zone</label>
              <input
                type="text"
                value={fallbackZone}
                onChange={(e) => setFallbackZone(e.target.value)}
                placeholder="Enter your location zone"
                className="w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
              />
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!formData.supplier_name || isSubmitting}
          className="w-full py-4 bg-primary text-white rounded-xl font-bold shadow-lg shadow-primary/30 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Supplier Information'}
        </button>
      </div>
    </div>
  );
}
